import { useEffect, useMemo, useState } from 'react';
import { Button, Skeleton } from '@indipia/ui-core';
import { useHolidays } from '../hooks/useHolidays';
import { presetRange, type DatePreset as Preset } from '../config/datePreset';
import { useWorklogStore } from '../store/worklogStore';
import type { BasicInfoPartner } from '../types/worklog.types';
import { PhotoLightbox } from './PhotoLightbox';
import { ThumbImg } from './ThumbImg';
import { Toast } from './Toast';
import { WorklogFeedGroups } from './worklog-feed/WorklogFeedGroups';
import { WorklogFeedToolbar } from './worklog-feed/WorklogFeedToolbar';
import { buildPairGroups, filterSortFeed, toDash, toFeedItems } from './worklog-feed/feedModel';
import { useFeedPartners } from './worklog-feed/useFeedPartners';
import { useIndiPhotoWork } from './worklog-feed/useIndiPhotoWork';
import { useWorklogFeed } from './worklog-feed/useWorklogFeed';

export interface SiteWorkFeedProps {
  /** 대상 현장 — 서버가 이 현장의 작업요청·현장보고만 내려준다(GET /timeline?siteId=) */
  siteId: number;
  /** 열람 전용(거래완료 현장) — 현장보고 사진을 사진첩에 담는 [사진선택]을 숨긴다(001 V2.6: 거래완료는 열람 전용으로 열림) */
  readOnly?: boolean;
}

/**
 * 나의 현장 작업공간(오른쪽 슬라이드)의 '공사현황' 탭 — 한 현장의 작업요청–작업보고 목록 (REQ-11-07, 001 E. 나의 현장).
 *
 * 조회 조건(기간 · 반장님 · 정렬)과 날짜(날씨)별 '작업요청 N건 | 작업보고 N건' 페어 행·점선 카드('작업요청 미열람' ·
 * '작업보고 미등록')·[사진선택](사진첩에 담기)은 공사현황 전체 화면과 같은 부품(`worklog-feed/`)을 쓴다.
 * 전체 화면과 달리 현장 목록 패널이 없고 현장은 호출부가 정해 준다.
 *
 * 인디공사현황 에이전트 레이아웃 밖(셸의 현장 작업공간 드로어)에서 마운트되므로 기준정보(반장 목록)를 직접 보장한다.
 */
export function SiteWorkFeed({ siteId, readOnly = false }: SiteWorkFeedProps) {
  // 기준정보(반장·공통코드) — WorkLogLayout 밖이라 여기서 보장한다. 이미 적재·조회 중이면 건너뛴다(멱등).
  useEffect(() => {
    void useWorklogStore.getState().load();
  }, []);

  // ── 조회조건(기간) — 기본 '이번주'(공사현황 전체 화면과 동일) ──
  const [preset, setPreset] = useState<Preset>('tw');
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
  const holidays = useHolidays();
  const range = customRange
    ? { from: new Date(`${customRange.from}T00:00:00`), to: new Date(`${customRange.to}T00:00:00`) }
    : presetRange(preset);

  // ── 결과 필터/보기 ──
  const [selectedPartners, setSelectedPartners] = useState<BasicInfoPartner[]>([]);
  const [sortDesc, setSortDesc] = useState(true);
  const { partners, partnersError } = useFeedPartners();

  // ── 피드 조회 — 서버로는 기간·현장만 보낸다. 반장 필터는 로드된 피드에 적용한다. ──
  const { feedItems, state, hasMore, loadingMore, reload, scrollRef, sentinelRef } = useWorklogFeed({
    dateFrom: toDash(range.from),
    dateTo: toDash(range.to),
    siteId,
  });

  const work = useIndiPhotoWork(feedItems);
  const [lb, setLb] = useState<{ frId: number; photoIdx: number } | null>(null);

  const selectedIds = useMemo(() => new Set(selectedPartners.map((p) => p.partnerId)), [selectedPartners]);
  const mergedItems = useMemo(
    () => filterSortFeed(toFeedItems(feedItems), { partnerIds: selectedIds, sortDesc }),
    [feedItems, selectedIds, sortDesc],
  );
  const groups = useMemo(() => buildPairGroups(mergedItems), [mergedItems]);

  const lbPhotos = useMemo(() => {
    if (!lb) return [];
    const fr = feedItems.find((f) => f.type === 'FIELD_REPORT' && f.fieldReport?.fieldReportId === lb.frId)?.fieldReport;
    return (fr?.photos ?? []).map((p) => ({ url: p.url || p.thumbUrl }));
  }, [lb, feedItems]);

  return (
    <div className="flex h-full w-full min-w-0 flex-col bg-surface">
      <WorklogFeedToolbar
        range={range}
        preset={preset}
        customRange={customRange}
        onPresetChange={setPreset}
        onCustomRangeChange={setCustomRange}
        holidays={holidays}
        partners={partners}
        partnersError={partnersError}
        selectedPartners={selectedPartners}
        onPartnersChange={setSelectedPartners}
        sortDesc={sortDesc}
        onToggleSort={() => setSortDesc((v) => !v)}
      />

      <div ref={scrollRef} className="flex-1 overflow-auto px-6 pb-10">
        {state === 'loading' && (
          <div className="space-y-3 pt-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={112} className="rounded-lg" />
            ))}
          </div>
        )}

        {state === 'error' && (
          <div className="mt-6 flex flex-col items-center gap-3 rounded-lg border border-border bg-surface-raised py-16">
            <span className="text-sm text-text-secondary">공사현황을 불러오지 못했습니다.</span>
            <Button variant="secondary" size="sm" onClick={reload}>
              다시 조회
            </Button>
          </div>
        )}

        {state === 'ready' && mergedItems.length === 0 && (
          <div className="mt-6 flex flex-col items-center gap-sm rounded-lg border border-border bg-surface-raised py-20 text-center">
            <div className="text-heading-sm font-bold text-text">표시할 내용이 없습니다</div>
            <div className="text-body-md leading-relaxed text-text-secondary">
              이 기간에 작업요청·작업보고가 없어요. 기간이나 반장님 조건을 바꿔보세요.
            </div>
          </div>
        )}

        {state === 'ready' && groups.length > 0 && (
          <WorklogFeedGroups
            groups={groups}
            work={work}
            readOnly={readOnly}
            onOpenPhoto={(frId, photoIdx) => setLb({ frId, photoIdx })}
          />
        )}

        {/* 무한스크롤 센티넬 + 추가 로딩 표시 */}
        {state === 'ready' && hasMore && (
          <div ref={sentinelRef} className="flex items-center justify-center py-5 text-body-sm text-text-muted">
            {loadingMore ? '불러오는 중…' : ''}
          </div>
        )}
      </div>

      <PhotoLightbox
        photos={lbPhotos}
        index={lb?.photoIdx ?? null}
        onClose={() => setLb(null)}
        onIndexChange={(i) => setLb((prev) => (prev ? { ...prev, photoIdx: i } : prev))}
        renderImage={(photo, style) => <ThumbImg url={photo.url} alt="" imgStyle={style} />}
      />

      <Toast message={work.toastMsg} onClose={work.clearToast} variant="info" />
    </div>
  );
}
